import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AlertsService } from '../../services/alerts.service';
import { AbortResponse, CaptureResponse, FingerprintService, ImageDataResponse, InitResponse, ScannerStatusResponse } from '../../services/fingerprint.service';
import { BaseService } from '../../shared/services/base.service';
import { AppService } from '../../shared/services/app.service';
import { DialogRef } from '../dialog/dialog-ref';
import { LoaderService } from '../../services/loader.service';
import { DialogConfig } from '../dialog/dialog-config';
import { Subject, Subscription, takeUntil, timer } from 'rxjs';

@Component({
    selector: 'app-fingerprint-capture',
    templateUrl: './fingerprint-capture.component.html',
    styleUrls: ['./fingerprint-capture.component.scss'],
    standalone: false
})
export class FingerprintCaptureComponent implements OnInit, OnDestroy {
  @Input() dataForCard: Array<any>;
  @Input() dialogData:any;
  @Output() fingerPrintOutput = new EventEmitter<any>();

  data: any;

  public isDeviceConnected = false;
  public imageBase64: string | undefined;
  private baseUrl: string | undefined;
  byteArray: any;
  action: any;
  isDisconnecting: boolean = false;
  configuredDeviceId: string | undefined;

  // Add state variables for device status
  public isCapturing: boolean = false;
  public isSensorOn: boolean = false;
  public isSensorValid: boolean = false;

  // Add subscriptions
  public statusSubscription: Subscription | undefined;
  public previewSubscription: boolean | undefined;

  // Flag to track if the user has clicked on capture fingerprint
  public hasClickedOnCaptureFingerprint: boolean = false;
  public destroy$ = new Subject<void>();

  constructor(
    public loader: LoaderService,
    public appService: AppService,
    private baseService: BaseService,
    private fingerprintService: FingerprintService,
    private alertService: AlertsService,
    private dialogRef: DialogRef,
    private dialogConfig: DialogConfig
  ) { }


  ngOnInit() {
    console.log("dataForCard:::" + this.dataForCard);
    console.log("hello:::" + this.dataForCard);
    this.fingerprintService.setDeviceInfo(undefined);
    this.getBaseURL();
  }

  /**
   * Fetches fingerprint scanner base URL from facility/system config.
   *
   * Reads `fingerprint_scanner_url` and stores it in `baseUrl`, which is
   * required before device initialization. If not configured, shows an error.
   */
  getBaseURL() {
    this.loader.showSpinner();
    const configData = {
      args: "module_code:patient-registration,config_code:fingerprint_scanner_url"
    };
    this.baseService.getData(configData, '', 'facility_config_mapping', 'facility-management', 'GET_CONFIG').subscribe({
      next: (res) => {
        this.loader.hideSpinner();
        if(res?.resource?.length > 0) {
          this.baseUrl = res?.resource[0]?.config_value;
          console.log(`Fingerprint Scanner URL found: ${this.baseUrl}`);

        } else {
          this.alertService.error("Fingerprint scanner URL not configured on System/Facility Admin.");
          return; 
        }
      },
      error: (err) => {
        this.loader.hideSpinner();
        console.error("Error fetching configuration:", err);
      },
    });
  }

  /**
   * Validates detected scanner against active machine master configuration.
   *
   * Queries fingerprint scanner config by `device_id` and ensures required SDK
   * fields are present. Resolves `true` when scanner is valid and stores
   * `configuredDeviceId`; otherwise resolves `false` and shows an error.
   */
  getMachineConfig(scannerName: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.loader.showSpinner();
      const machineConfigFilter = {
        filter: `machineType_code:fingerprint_scanner,status:active,device_id:${scannerName}`
      };

      this.baseService.getData(machineConfigFilter, '', 'machine', 'facility-management', 'GET_PAGE').subscribe({
        next: (configData: any) => {
          if (configData && configData?.errCode === 0 && configData?.resource && configData?.resource?.length > 0) {
            this.loader.hideSpinner();
            const machineConfig = configData?.resource[0];
            console.log("Device found in the system:", machineConfig);
            if (machineConfig?.machine_sdk_url && machineConfig?.device_id) {
              console.log(`Loaded machine config: SDK URL = ${machineConfig?.machine_sdk_url}, Device ID = ${machineConfig?.device_id}`);
              resolve(true);
            } else {
              this.alertService.error("SDK is not configured in the master.");
              resolve(false);
            }
          } else {
            this.alertService.error("No active fingerprint scanner configuration found.");
            resolve(false);
          }
        },
        error: (err) => {
          this.loader.hideSpinner();
          console.error("Error fetching machine configuration:", err);
          resolve(false);
        },
        complete: ()=>{
          this.loader.hideSpinner();
          if (this.dialogConfig.data != undefined) {
          this.data = this.dialogConfig.data;
          if(!this.action && this.data){
            this.action = this.data.action
            this.data = this.data.item
          }
        }
        setTimeout(() => {
          if(this.dialogData){
            console.log("event is :: patientResult this.dialogData", this.dialogData);
            this.action = this.dialogData.action
            this.data = this.dialogData['item']
          }
        }, 4000)
        }
      });
    });
  }

  deviceConnected(): boolean {
    return this.isDeviceConnected;
  }

  public get deviceInfo() {
    return this.fingerprintService.getDeviceInfo();
  }

  // Detect and initialize the fingerprint device
  detectDevice(): void {

    if (!this.baseUrl) {
      this.alertService.error("Fingerprint scanner URL not configured on System/Facility Admin.");
      return;
    }

    this.alertService.info("Detecting device...");
    this.fingerprintService.setBaseUrl(this.baseUrl);
    this.fingerprintService.setPageId(Math.random().toString());
    this.fingerprintService.initDevice().subscribe({
      next: async (response: InitResponse) => {
        console.log("Init response:", response);
        if (response?.retString == "Success" && response?.ScannerInfos && response?.ScannerInfos?.length > 0) {
          let scannerInfo = response?.ScannerInfos[0];
          let scannerId = '';
          if (typeof scannerInfo?.ScannerName === 'string' && scannerInfo?.ScannerName.trim() !== '') {
            scannerId = scannerInfo.ScannerName;
          }
          if (!scannerId) {
            console.error("Detected scannerInfo has no ScannerName.", scannerInfo);
            return;
          }

          if (!(await this.getMachineConfig(scannerId))) {
            this.alertService.error(`Detected device ID ('${scannerId}') does not match configured in master.`);
            return;
          }

          this.fingerprintService.setDeviceInfo(scannerInfo);
          this.isDeviceConnected = true;
          this.startStatusPolling();

        } else {
          this.alertService.error("No device detected.");
          console.error("No device detected or error in initDevice");
        }
      },
      error: (err) => {
        this.alertService.error("No device detected.");
        console.log("Error detecting device:", err)
      }
    });
  }

  stringToBoolean(value: string | boolean ): boolean {
    if (typeof value === 'string') {
      if (value.toLowerCase() === 'true') {
        return true;
      } else if (value.toLowerCase() === 'false') {
        return false;
      }
    } else if (typeof value === 'boolean') {
      return value;
    }
    console.warn(`Invalid boolean value: ${value}`);
    return false;
  }

  private startStatusPolling(): void {
    // Ensure only one status subscription is active at a time
    if (this.statusSubscription && !this.statusSubscription.closed) {
      return;
    }
    if (!this.deviceInfo) {
      console.warn("Device not initialized, status polling skipped.");
      return; // Don't start polling if device is not initialized
    }

    this.statusSubscription = timer(0, 2000).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        if(this.isDisconnecting || !this.deviceConnected){
          return;
        }
        this.fingerprintService.getScannerStatus().subscribe({
          next: (status: ScannerStatusResponse) => {
            console.log("Scanner status response:", status);
            
            this.isCapturing = this.stringToBoolean(status.IsCapturing);
            this.isSensorOn = this.stringToBoolean(status.SensorOn);
            this.isSensorValid = this.stringToBoolean(status.SensorValid);

            console.log(`Status: Capturing=${this.isCapturing}, SensorOn=${this.isSensorOn}, SensorValid=${this.isSensorValid}`);

            if (this.isCapturing && this.isSensorOn && this.isSensorValid && !this.previewSubscription) {
              this.alertService.info("Starting preview polling...");
              this.startPreviewPolling();
            } else if (!this.isCapturing && this.previewSubscription && this.hasClickedOnCaptureFingerprint) {
              this.stopPreviewPolling();
              this.getImageData();
              this.hasClickedOnCaptureFingerprint = false;
              this.alertService.success("Capture complete.");

              if (status.retValue !== 0 && status.retValue !== "0") {
                if (status.retValue === -112 || status.retValue === "-112") {
                  this.alertService.error("Device disconnected. Please reconnect and try again.");
                } else {
                  console.warn(`Scanner status error: ${status.retString} (Code: ${status.retValue})`);
                }
                this.disconnectDevice();
              }
            }
          },
          error: (httpError) => {
            console.error("Error fetching scanner status:", httpError);
            this.disconnectDevice();
          }
        });
      },
      error: (timerError) => {
        console.error("Error in status polling timer:", timerError);
        this.disconnectDevice();
      }
    });
  }

  // Start continuous preview polling
  startPreviewPolling(): void {
    if (this.previewSubscription) {
      return;
    }

    if (!this.deviceInfo) {
      console.warn("Device not initialized, preview polling skipped.");
      return; // Don't start polling if device is not initialized
    }

    this.previewSubscription = true;
  }

  // Stop status polling
  stopStatusPolling(): void {
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
      this.statusSubscription = undefined;
      console.log("Status polling stopped.");
    }
  }

  // Stop preview polling
  stopPreviewPolling(): void {
    if (this.previewSubscription) {
      this.previewSubscription = undefined;
      console.log("Preview polling stopped.");
    }
  }

  // Stop all polling
  stopPolling(): void {
    this.stopStatusPolling();
    this.stopPreviewPolling();
  }

  // Capture fingerprint using startCapture and then retrieve the image data
  captureFingerprint(): void {
    if (!this.deviceConnected()) {
      console.error("Device not connected. Please detect the device first.");
      return;
    }

    this.imageBase64 = "";

    this.alertService.info("Placed your finger on device.");
    this.fingerprintService.startCapture().subscribe({
      next: (captureResponse: CaptureResponse) => {
        console.log("start Capture response:", captureResponse);
        if (captureResponse.retValue === "0") {
         console.log("captureSingle successful.");
         this.hasClickedOnCaptureFingerprint = true;
        } else {
          console.error("Error in startCapture");
          this.hasClickedOnCaptureFingerprint = true;
        }
      },
      error: (err) => {
        console.error("Error calling startCapture:", err);
        this.hasClickedOnCaptureFingerprint = true;
      }
    });
  }

  /**
   * Disconnects the fingerprint device by stopping polling,
   * aborting any capture in progress, and uninitializing the device.
   */
  disconnectDevice(): void {
    if (this.isDisconnecting) {
      console.log("Disconnection already in progress.");
      return;
    }

    if (!this.isDeviceConnected && (!this.statusSubscription || this.statusSubscription.closed)) {
      console.log("Device already disconnected or not initialized.");
      this.resetDeviceState();
      return;
    }

    this.isDisconnecting = true;
    console.log("Starting device disconnection process...");

    this.stopPolling();

    this.fingerprintService.abortCapture().pipe(takeUntil(this.destroy$)).subscribe({
      next: (abortResponse: AbortResponse) => {
        console.log("Abort response:", abortResponse);
        if (abortResponse.retValue === "0") {
          console.log("Capture aborted successfully.");
        } else {
          console.error("Error aborting capture");
        }
        this.unInitializeDevice();
      },
      error: (err) => {
        console.error("Error aborting capture:", err);
        this.unInitializeDevice();
      }
    });
  }

  /**
   * Uninitializes the fingerprint device and resets component state.
   * This is separated from the disconnectDevice method for better code organization.
   */
  unInitializeDevice(): void {
    this.fingerprintService.uninitDevice().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        console.log("Uninit response:", res);
        if (res.retValue === "0") {
          this.alertService.success("Device disconnected successfully.");
        } else {
          console.error("Error uninitializing device");
        }
        this.resetDeviceState();
      },
      error: (err) => {
        console.error("Error uninitializing device:", err);
        this.alertService.error("Error disconnecting device.");
        this.resetDeviceState();
      }
    });
  }

  /**
   * Resets all device-related state variables to their default values.
   */
  resetDeviceState(): void {
    this.isDeviceConnected = false;
    this.isSensorOn = false;
    this.isSensorValid = false;
    this.isCapturing = false;
    this.hasClickedOnCaptureFingerprint = false;
    this.isDisconnecting = false;
    this.fingerprintService.setDeviceInfo(undefined);
  }

  getImageData(){
    this.fingerprintService.getImageData(1, 300, 400, 2.5).subscribe({
      next: (imgResponse: ImageDataResponse) => {
        console.log("Image data response:", imgResponse);
        if (imgResponse.retValue === "0") {
          this.imageBase64 = imgResponse.imageBase64;
          console.log("Captured image saved");
        } else {
          console.error("Error retrieving image data");
        }
      },
      error: (err) => {
        console.error("Error in getImageData:", err);
      }
    });
  }
  closePopup(savedFingerprintId) {
    this.dialogRef.close(savedFingerprintId);
  }
  submitFingerprint() {
    this.loader.showSpinner();
    console.log("before API this.data", this.data);
    this.data = {
      "account_id": this.appService.getOrganization().account_id,
      "facility_id": this.appService.getOrganization().org_id,
      "uhId": this.data?.uhId,
      "patient_id": this.data && this.data.patient_id ? this.data.patient_id : this.data?.id,
      "imageBase64": this.imageBase64,// This should be the actual base64 image string
      "device_id": this.deviceInfo ? this.deviceInfo.DeviceType : '',
      "device_name": this.deviceInfo ? this.deviceInfo.ScannerName : ''
    };
    let action = "add";
    let message = "Fingerprint added successfully";
    if (this.action?.action_name == 'matchFingerPrint') {
      action = "matchFingerPrint";
      message = "Fingerprint matched successfully";
    }
    console.log("after submit this.data", this.data);
    this.baseService.action(this.data, action, 'aimlsuit', 'fingerprint').subscribe((data: any) => {
      if (data.errCode != 0) {
        this.alertService.error(data.message);
        this.loader.hideSpinner();
        return;
      }
      if (data.errCode == 0 || data.resource.length != 0) {
        this.alertService.success(message, undefined, true);
        this.loader.hideSpinner();
        // Return the DB ID to the parent component (using dialogRef)
        const savedFingerprintId = data.resource[0].id; // Assuming the response contains the ID
        // this.dialogRef.close(savedFingerprintId); // Pass the ID back to the parent component
        this.closePopup(savedFingerprintId);
        this.fingerPrintOutput.emit(savedFingerprintId);
      }
    },
      error => {
        console.log(error);
      }
    );
  }

  ngOnDestroy(): void {
    this.stopPolling();

    // Then abort capture and wait for it to complete before closing
    this.fingerprintService.abortCapture()
      .pipe(takeUntil(this.destroy$)) // This ensures abort itself gets cleaned up if needed
      .subscribe({
        next: () => {
          console.log('Capture aborted on component destroy');
          
          // Now uninitialize the device
          this.fingerprintService.uninitDevice()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.resetDeviceState();
                console.log('Device uninitialized on component destroy');
                // Only close the popup after all cleanup is done
                this.closePopup(this.data?.id ?? this.data?.fingerprint_id);
              },
              error: (err) => {
                this.resetDeviceState();
                console.warn('Error uninitializing device:', err);
                // Still close the popup
                this.closePopup(this.data?.id ?? this.data?.fingerprint_id);
              }
            });
        },
        error: (err) => {
          console.warn('Error aborting capture on component destroy:', err);
          // Try to uninit anyway
          this.fingerprintService.uninitDevice()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.resetDeviceState();
                console.log('Device uninitialized after abort error');
                this.closePopup(this.data?.id ?? this.data?.fingerprint_id);
              },
              error: (uninitErr) => {
                this.resetDeviceState();
                console.warn('Error uninitializing device after abort error:', uninitErr);
                this.closePopup(this.data?.id ?? this.data?.fingerprint_id);
              }
            });
        }
      });
      
    // Finally, trigger the destroy subject to clean up any remaining subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }
}
